import { useRouter as useNextRouter } from 'next/router';
import { useMemo } from 'react';

/**
 * Pages Router stand-ins for the three `next/navigation` hooks the app used
 * under the App Router, so call sites keep the same shape.
 */
export { useRouter } from 'next/router';

/** The current URL path, without query string or hash. */
export const usePathname = (): string => {
  const { asPath, isReady } = useNextRouter();

  if (typeof window !== 'undefined' && !isReady) {
    return window.location.pathname;
  }

  return asPath.split('?')[0].split('#')[0];
};

/**
 * The current search params.
 *
 * Neither `router.query` nor `router.asPath` can be trusted on the first
 * render of a statically optimised page: `query` is `{}` and `asPath` is the
 * bare route until `router.isReady` flips. Callers here read params during
 * render — picking which splash screen to show, for instance — so an empty
 * first result silently sends them down the wrong branch and, because the
 * choice is made once, they never recover.
 *
 * The live URL is correct from the very first client render, so read that.
 * `asPath` and `isReady` are dependencies purely to recompute on navigation
 * and once the router hydrates.
 */
export const useSearchParams = (): URLSearchParams => {
  const { asPath, isReady } = useNextRouter();

  return useMemo(() => {
    if (typeof window === 'undefined') {
      return new URLSearchParams(asPath.split('?')[1]?.split('#')[0] ?? '');
    }

    return new URLSearchParams(window.location.search);
  }, [asPath, isReady]);
};
